import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Image from "@tiptap/extension-image";
import { useEffect } from "react";
import { api, authedUrl } from "@/lib/api";
import { toast } from "sonner";
import { Bold, Italic, Strikethrough, List, ListOrdered, Link as LinkIcon, Quote, Heading2, Undo2, Redo2, Code, Image as ImageIcon } from "lucide-react";

/**
 * Lightweight rich-text editor built on TipTap.
 *
 * `value` — HTML string
 * `onChange(html)` — fires on every edit
 * `placeholder` — passive hint shown when the doc is empty
 *
 * Image handling: pasting a screenshot (or dragging an image file in)
 * uploads it to `/api/tickets/inline-upload` and inserts an <img> with the
 * returned URL so the image becomes part of the description body.
 */
export default function RichEditor({ value, onChange, placeholder = "Write something…", testid, minHeight = 160 }) {
  const uploadInline = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/tickets/inline-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    // The returned URL is API-scoped; we need an auth-aware public URL for <img>.
    return authedUrl(data.url);
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { class: "text-primary underline" } }),
      Image.configure({ HTMLAttributes: { class: "rounded-md max-w-full my-2 cursor-zoom-in" } }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "rich-editor-body prose prose-sm dark:prose-invert max-w-none focus:outline-none",
        "data-testid": testid ? `${testid}-body` : undefined,
      },
      handlePaste(view, event) {
        // Intercept clipboard image items BEFORE TipTap turns them into data URLs.
        const items = Array.from(event.clipboardData?.items || []);
        const imgItems = items.filter((it) => it.type.startsWith("image/"));
        if (!imgItems.length) return false;
        event.preventDefault();
        (async () => {
          for (const it of imgItems) {
            const file = it.getAsFile();
            if (!file) continue;
            try {
              const url = await uploadInline(file);
              view.dispatch(view.state.tr.replaceSelectionWith(
                view.state.schema.nodes.image.create({ src: url, alt: file.name || "pasted image" })
              ));
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Image upload failed");
            }
          }
        })();
        return true;
      },
      handleDrop(view, event) {
        const files = Array.from(event.dataTransfer?.files || []).filter((f) => f.type.startsWith("image/"));
        if (!files.length) return false;
        event.preventDefault();
        (async () => {
          for (const file of files) {
            try {
              const url = await uploadInline(file);
              view.dispatch(view.state.tr.replaceSelectionWith(
                view.state.schema.nodes.image.create({ src: url, alt: file.name })
              ));
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Image upload failed");
            }
          }
        })();
        return true;
      },
    },
  });

  // Keep the editor in sync when parent replaces `value` (e.g. dialog open/reset).
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value !== current) editor.commands.setContent(value || "", false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;
  const btn = (active, onClick, Icon, title, tid) => (
    <button
      type="button"
      title={title}
      data-testid={tid}
      onClick={onClick}
      className={`h-7 w-7 flex items-center justify-center rounded transition-colors ${active ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-accent"}`}
    >
      <Icon className="w-3.5 h-3.5" />
    </button>
  );

  const setLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("URL", prev || "https://");
    if (url === null) return;
    if (url === "") return editor.chain().focus().extendMarkRange("link").unsetLink().run();
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const pickImage = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const url = await uploadInline(file);
        editor.chain().focus().setImage({ src: url, alt: file.name }).run();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Image upload failed");
      }
    };
    input.click();
  };

  return (
    <div className="rich-editor rounded-md border bg-background/40" data-testid={testid}>
      <div className="flex items-center gap-1 border-b px-1.5 py-1 flex-wrap">
        {btn(editor.isActive("heading", { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), Heading2, "Heading")}
        {btn(editor.isActive("bold"), () => editor.chain().focus().toggleBold().run(), Bold, "Bold (Ctrl+B)")}
        {btn(editor.isActive("italic"), () => editor.chain().focus().toggleItalic().run(), Italic, "Italic (Ctrl+I)")}
        {btn(editor.isActive("strike"), () => editor.chain().focus().toggleStrike().run(), Strikethrough, "Strikethrough")}
        {btn(editor.isActive("bulletList"), () => editor.chain().focus().toggleBulletList().run(), List, "Bulleted list")}
        {btn(editor.isActive("orderedList"), () => editor.chain().focus().toggleOrderedList().run(), ListOrdered, "Numbered list")}
        {btn(editor.isActive("blockquote"), () => editor.chain().focus().toggleBlockquote().run(), Quote, "Quote")}
        {btn(editor.isActive("code"), () => editor.chain().focus().toggleCode().run(), Code, "Inline code")}
        {btn(editor.isActive("link"), setLink, LinkIcon, "Link")}
        {btn(false, pickImage, ImageIcon, "Insert image (or paste from clipboard)", `${testid}-image`)}
        <div className="flex-1" />
        {btn(false, () => editor.chain().focus().undo().run(), Undo2, "Undo")}
        {btn(false, () => editor.chain().focus().redo().run(), Redo2, "Redo")}
      </div>
      <div style={{ minHeight }} className="px-3 py-2 text-sm">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
